import { useContext, useEffect } from "react";
import { FaEnvelope, FaPhone, FaLocationDot, FaLinkedinIn } from "react-icons/fa6";
import { GlobalStateContext } from "../controller/GlobalState";
import { company, emailaddress } from "../controller/global";

// A real, crawlable contact page. The site used to have only a modal form, so
// /contact returned 404 and the business's name, address and phone lived only
// in the footer and in structured data — no page a crawler, a directory
// listing or a Google Business Profile could point at as the place the
// company is.
//
// No embedded map, deliberately. A map built from an address search geocoded
// the old office to a different building entirely, and the embed's info card
// showed that wrong address right next to the correct one. A mis-pinned map
// contradicts the NAP it is meant to reinforce. Add one back from the Google
// Business Profile's own place ID once the profile is verified, not from an
// address search.

// Everything below is already published elsewhere on the site: the response
// time on /faq, the delivery stages on /how-we-work-and-function, and the
// service areas on the homepage. The page was 305 words — the thinnest on the
// site, and the one a local search result points at.
const NEXT_STEPS = [
  { t: "You tell us what you are building", d: "A short message is enough: what the software has to do, any deadline, and whether something already exists that it must work with." },
  { t: "We reply within one business day", d: "Usually with questions. If it is not a fit, we say so at this point rather than after a proposal." },
  { t: "You get a written scope and estimate", d: "Stages, timeline and cost, based on our seven-stage delivery process — before any work starts." },
];

const SERVICE_AREAS = ["New Delhi", "Delhi NCR", "Noida", "Gurugram", "Faridabad", "Greater Noida", "Across India", "United States", "United Kingdom"];

const FAQS = [
  { q: "Where is InQmise Technologies located?", a: "Our office is at Cabin No. 10, Second Floor, A 24/9, Mohan Cooperative, Badarpur, New Delhi 110044, India. We are an MSME-registered company working from Delhi NCR." },
  { q: "Do you work with clients outside Delhi NCR?", a: "Yes. We work remotely with clients across India, the United States and the United Kingdom, with overlap hours agreed at kickoff. For clients in Delhi NCR, Noida and Gurugram we also meet in person." },
  { q: "How quickly will someone reply?", a: "Within one business day. You can also call +91 81304 09466 or email contact@inqmisetechnologies.com directly." },
];

const Contact = () => {
  const { setIsOpen } = useContext(GlobalStateContext);

  useEffect(() => {
    document.title = "Contact InQmise Technologies | Software Company in Delhi NCR";

    const description = document.createElement("meta");
    description.name = "description";
    description.content =
      "Contact InQmise Technologies in Badarpur, New Delhi: phone, email and office address, or start a project brief for software, web, mobile, cloud or AI work.";
    document.head.appendChild(description);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact InQmise Technologies",
      "url": "https://inqmisetechnologies.com/contact",
      "about": { "@id": "https://inqmisetechnologies.com/#organization" },
      "mainEntity": { "@id": "https://inqmisetechnologies.com/#organization" },
    });
    document.head.appendChild(schema);

    const breadcrumb = document.createElement("script");
    breadcrumb.type = "application/ld+json";
    breadcrumb.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://inqmisetechnologies.com/contact" },
      ],
    });
    document.head.appendChild(breadcrumb);

    return () => {
      description.remove();
      schema.remove();
      breadcrumb.remove();
    };
  }, []);

  return (
    <main className="editorial-ai pt-20">
      <section className="ea-wrap ea-section contact-page">
        <div className="contact-grid">
          <div className="contact-details">
            <p className="ea-eyebrow">Contact</p>
            <h1>Talk to InQmise Technologies</h1>
            <p className="ea-lede">
              Tell us what you are building. We work with clients in India and abroad, and we will be
              straight with you about fit before anything is committed.
            </p>

            <dl className="contact-nap">
              <div>
                <dt><FaLocationDot aria-hidden="true" /> Office</dt>
                <dd>
                  <address>
                    {company.legalName}<br />
                    {company.street},<br />
                    {company.locality}, {company.region} {company.postalCode}<br />
                    {company.country}
                  </address>
                </dd>
              </div>
              <div>
                <dt><FaPhone aria-hidden="true" /> Phone</dt>
                <dd><a href={`tel:${company.phoneHref}`}>{company.phone}</a></dd>
              </div>
              <div>
                <dt><FaEnvelope aria-hidden="true" /> Email</dt>
                <dd><a href={`mailto:${emailaddress}`}>{emailaddress}</a></dd>
              </div>
              <div>
                <dt><FaLinkedinIn aria-hidden="true" /> LinkedIn</dt>
                <dd>
                  <a href="https://www.linkedin.com/company/inqmise-technologies" target="_blank" rel="noopener noreferrer">
                    linkedin.com/company/inqmise-technologies
                  </a>
                </dd>
              </div>
            </dl>

            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={() => setIsOpen(true)} aria-label="Open the project brief form">
                Start a project brief ↗
              </button>
            </div>
          </div>

        </div>
      </section>

      <section className="ea-wrap ea-section">
        <div className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What happens next</div>
            <h2>From your first message<br />to a written estimate.</h2>
          </div>
          <p>No sales sequence. Three steps, and you know where you stand.</p>
        </div>
        <div className="aa-pill-grid">
          {NEXT_STEPS.map((step, i) => (
            <div className="ai-industry-card" key={step.t} style={{ flex: "1 1 280px" }}>
              <h3>{`${i + 1}. ${step.t}`}</h3>
              <p>{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ea-wrap ea-section">
        <div className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where we work</div>
            <h2>Delhi NCR in person,<br />the rest remotely.</h2>
          </div>
          <p>Our office is in Badarpur, South Delhi. Most delivery happens remotely, as code, runbooks and dashboards.</p>
        </div>
        <div className="cs-tags-row">
          {SERVICE_AREAS.map((area) => (
            <span key={area} className="cs-tag" style={{ fontSize: "var(--t-xs)", padding: "12px 20px" }}>{area}</span>
          ))}
        </div>
      </section>

      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <div className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Before you get in touch.</h2>
          </div>
          {FAQS.map((item) => (
            <div className="ea-faq-row" key={item.q}>
              <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>{item.q}</h3>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Contact;
